import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useMutation } from 'react-query';
import { ManualTemplatesApi, ManualTemplatesQueries, ManualTemplatesTypes } from '@frontend/api-manual-templates';
import { NotificationSettingsTypes } from '@frontend/api-notification-settings';
import { DefaultTagKeys, tagList, standardizeTemplateTextTags } from '@frontend/api-template-tags';
import { TFunction, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { ContentLoader, Heading, TagType, useAlert } from '@frontend/design-system';
import { EditMessageTemplate } from '../../components/EditMessageTemplate';

const { ManualTemplateTypes } = ManualTemplatesTypes;

type RequestReceivedMessageTemplateProps = {
  locationId: string;
};

const confirmationTemplateTagsList = (t: TFunction, locationName?: string): TagType[] =>
  tagList({
    t,
    locationName,
    apptDatetime: dayjs().add(1, 'week').hour(10).minute(0),
    wantedTags: [
      DefaultTagKeys.preferredName,
      DefaultTagKeys.firstName,
      DefaultTagKeys.lastName,
      DefaultTagKeys.practiceName,
      DefaultTagKeys.date,
      DefaultTagKeys.time,
      DefaultTagKeys.apptDatetime,
    ],
  });

export const AppointmentConfirmedMessageTemplate = ({ locationId }: RequestReceivedMessageTemplateProps) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule-settings');
  const { getScopeName } = useAppScopeStore();

  const {
    data: confirmationTemplateData,
    refetch: refetchConfirmationTemplate,
    isFetching,
  } = ManualTemplatesQueries.useGetManualTemplateByType({
    templateType: ManualTemplateTypes.onlineSchedulingConfirmation,
    locationId,
  });

  const confirmationTemplateTags = useMemo(
    () => confirmationTemplateTagsList(t, getScopeName(locationId)),
    [t, getScopeName, locationId]
  );

  const defaultConfirmationTemplate = t(
    "Hello, this is {{practice_name}}. This text is to confirm that we've approved your appointment request for {{appt_datetime}}. See you then, {{preferred_name}}.",
    {
      practice_name: '{practice_name}',
      appt_datetime: '{appt_datetime}',
      preferred_name: '{preferred_name}',
    }
  );

  const updateConfirmationTemplate = useMutation({
    mutationFn: ({ newTemplateText, templateSubType }: NotificationSettingsTypes.UpdateTemplateProps) => {
      const standardizedTemplateText = standardizeTemplateTextTags(t, newTemplateText);
      return ManualTemplatesApi.updateManualTemplateByType(templateSubType, standardizedTemplateText, locationId);
    },
    onSuccess: () => {
      alert.success(t('Success, updated template'));
      refetchConfirmationTemplate();
    },
    onError: () => {
      alert.error(t('Failed to update template'));
    },
  });

  return (
    <>
      <ContentLoader show={isFetching} />
      <Heading level={2}>{t('Customize Your Message')}</Heading>
      <EditMessageTemplate
        defaultTemplateText={defaultConfirmationTemplate}
        initialTemplate={{
          template: {
            text: confirmationTemplateData?.Template || '',
            multi_template_text: confirmationTemplateData?.Template || '',
            type: 'all',
          },
        }}
        templateSubType={ManualTemplateTypes.onlineSchedulingConfirmation}
        tags={confirmationTemplateTags}
        onSave={updateConfirmationTemplate}
      />
    </>
  );
};
